
import apiClient from './api';

export const fetchTotalCount = async (domainId, year = null) => {
  try {
    const params = { domainId };
    if (year) params.year = year;
    const response = await apiClient.get('/api/accounts/totalcountbydomain', { params });
    return response.data;
  } catch{
    // throw new Error("Erreur lors de la récupération du total des comptes");
  }
};


/**
 * Récupère le nombre de comptes par type de compte avec filtre année
 * @param {number} domainId - ID du domaine
 * @param {number|null} year - Année optionnelle pour le filtre
 * @returns {Promise<Array>} Liste des comptes par type
 */
export const getAccountsCountByType = async (domainId, year = null) => {
  try {
    const params = { domainId };
    if (year) params.year = year;
    
    const response = await apiClient.get('/api/accounts/countbytype', { params });
    return response.data;
  } catch {
    // console.error('Erreur dans getAccountsCountByType:', error);
    // throw new Error("Erreur lors de la récupération des comptes par type");
  }
};



/**
 * Récupère la liste paginée des comptes avec filtres
 * @param {Object} params - Paramètres de recherche et pagination
 * @param {number} params.domainId - ID du domaine
 * @param {number} params.pageNumber - Numéro de page (défaut: 1)
 * @param {number} params.pageSize - Taille de page (défaut: 10)
 * @param {string} params.searchTerm - Terme de recherche
 * @param {string} params.accountTypeFilter - Filtre par type de compte
 * @param {string} params.statusFilter - Filtre par statut de validation
 * @param {string} params.plainCode - Filtre par code compte
 * @param {string} params.accountName - Filtre par nom de compte
 * @returns {Promise<Object>} Réponse paginée
 */
export const getAccounts = async (params = {}) => {
  try {
    const {
      domainId,
      pageNumber = 1,
      pageSize = 10,
      searchTerm,
      accountTypeFilter,
      statusFilter,
      plainCode,
      accountName,
      isManagedAccount = null // NOUVEAU FILTRE
    } = params;

    // Construction des paramètres de requête
    const queryParams = {
      pageNumber,
      pageSize,
      domainId
    };

    // Ajout des filtres conditionnels
    if (searchTerm) {
      if (/^\d+$/.test(searchTerm)) {
        queryParams.plainCode = searchTerm;
      } else {
        queryParams.accountName = searchTerm;
      }
    }

    if (accountTypeFilter) {
      queryParams.accountTypeName = accountTypeFilter;
    }

    if (statusFilter) {
      queryParams.status = parseInt(statusFilter);
    }

    if (plainCode) {
      queryParams.plainCode = plainCode;
    }

    if (accountName) {
      queryParams.accountName = accountName;
    }

    // CORRECTION : Ajouter le filtre isManagedAccount
    if (isManagedAccount !== null) {
      queryParams.isManagedAccount = isManagedAccount.toString() === 'true';
    }
    const response = await apiClient.get('/api/Accounts/all', { params: queryParams });
    return {
      success: true,
      data: response.data,
      items: response.data.items || [],
      totalCount: response.data.totalCount || 0,
      totalPages: response.data.totalPages || 1,
      currentPage: response.data.currentPage || 1
    };

  } catch (error) {
    // console.error('Erreur getAccounts:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erreur lors de la récupération des comptes',
      items: [],
      totalCount: 0
    };
  }
};

// Alias pour la compatibilité avec l'ancien code
export const getAccountsByDomainId = async (domainId, params = {}) => {
  return await getAccounts({ ...params, domainId });
};


export const createAccount = async (formData) => {
  try {
    // console.log("Envoi de la requête à /api/accounts");
    const response = await apiClient.post('/api/accounts', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
      },
    });
    // console.log("Réponse du serveur:", response.data);
    return response.data;
    
  } catch (error) {
    // console.error("Erreur complète de l'API:", error);
    const errorData = error.response?.data;
    // console.error("Données d'erreur du serveur:", errorData);
    
    if (errorData?.Errors) {
      // Erreurs de validation détaillées par champ
      throw new AccountValidationError(
        "Erreurs de validation",
        Array.isArray(errorData.errors)
          ? errorData.errors
          : Object.values(errorData.errors)
      );
    } else if (errorData?.Message) {
      // Erreur spécifique du backend
      throw new Error(errorData.Message);
    } else {
      // Erreur générique
      throw new Error(errorData?.message || "Une erreur s'est produite lors de la création du compte");
    }
  }
};

// Mise à jour d'un compte
export const updateAccount = async (id, formData) => {
  try {
    const response = await apiClient.put(`/api/Accounts/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    const errorData = error.response?.data;
    
    if (errorData?.Errors) {
      throw new AccountValidationError("Erreurs de validation", errorData.Errors);
    } else if (errorData?.Message) {
      throw new Error(errorData.Message);
    } else {
      throw new Error(errorData?.message || "Une erreur s'est produite lors de la mise à jour du compte");
    }
  }
};

// Récupérer les données d'un compte pour modification
// Récupérer les données d'un compte pour modification
// service/api/accountService.js
export const getAccountForUpdate = async (accountId) => {
  try {
    const response = await apiClient.get(`/api/Accounts/for-update/${accountId}`);
    
    return response.data;
  } catch (error) {
    // console.error("Erreur lors de la récupération des données du compte:", error);
    
    if (error.response?.status === 404) {
      throw new Error("Compte non trouvé");
    } else if (error.response?.status === 403) {
      throw new Error("Vous n'avez pas les permissions pour modifier ce compte");
    } else {
      throw new Error("Erreur lors de la récupération des données");
    }
  }
};

// Classe d'erreur pour les validations
export class AccountValidationError extends Error {
  constructor(message, errors) {
    super(message);
    this.name = "AccountValidationError";
    this.errors = errors;
  }
}


// Service pour incrémenter le cash d'un compte


/**
 * Met à jour le cash d'un compte avec upload de preuve
 * @param {number} accountId - ID du compte
 * @param {number} amountToAdd - Montant à ajouter
 * @param {File} proofFile - Fichier de preuve (optionnel)
 * @returns {Promise<boolean>}
 */
export const updateAccountCash = async (accountId, amountToAdd, proofFile = null) => {
  if (!accountId || isNaN(Number(accountId))) throw new Error("ID de compte invalide");
  if (!amountToAdd || isNaN(Number(amountToAdd))) throw new Error("Montant invalide veillez renseigner un montant valide dans le champ");
  
  try {
    // Création FormData pour gérer le fichier
    const formData = new FormData();
    formData.append('AccountId', accountId.toString());
    formData.append('NewCashAmount', amountToAdd.toString());
    
    if (proofFile) {
      formData.append('PreuveCash', proofFile);
    }

    await apiClient.put('/api/Accounts/Ajoutcash', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return true;
  } catch (error) {
    // console.error("Erreur lors de la mise à jour du cash :", error.response?.data || error.message);
    const errorMessage = error.response?.data?.message ||
                         error.response?.data ||
                         error.message ||
                         "Erreur inconnue lors de la mise à jour du cash";
    throw new Error(errorMessage);
  }
};

export const getAccountById = async (id) => {
  try {
    const response = await apiClient.get(`/api/Accounts/${id}`);
    return response.data; 
  } catch (error) {
    // console.log("Erreur lors de la recuperation des comptes : ", error.response?.data || error.message);
    throw new Error("An error occurred while updating the account check.",error);

  }
};

export const getTotalAccountCount = async () => {
  try {
      const response = await apiClient.get('/api/Accounts/count');
      return response.data;
  } catch (error) {
      console.error("Erreur compteur comptes :", error);
      throw error;
  }
};

export const updateAccountCheck = async (userData) => {
  try {
      const response = await apiClient.put(`/api/Accounts/update/check`, userData);
      return response.data;
  } catch (error) {
    throw new Error("An error occurred while updating the account check.",error);
  }
};

// listes des accounts 
export const fetchAccounts = async (params) => {
  try {
      const response = await apiClient.get('api/Accounts/all', { params });
      return response.data;
  } catch (error) {
      throw new Error("An error occurred while fetching the accounts.",error);
  }
};

// gerer les comptes qui possedent un titre
export const getAccountBySecurityPrivate = async (domainId) => {
  try {
    const response = await apiClient.get(`/api/Security/AccountBySecurityPrivate/${domainId}`);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération des comptes privés :", error);
    throw error;
  }
};

export const getAccountBySecurityMonetary = async (domainId) => {
  try {
    const response = await apiClient.get(`/api/Security/AccountBySecurityMonetary/${domainId}`);
    return response.data;
  } catch (error) {
    throw new Error("Erreur de recuperation",error);
  }
};

export const getAccountBySecurityFinancial = async (domainId) => {
  try {
    const response = await apiClient.get(`/api/Security/AccountBySecurityFinancial/${domainId}`);
    return response.data;
  } catch (error) {
    throw new Error("erreur de recuperation", error);
  }
};

export default {
  getAccounts
};